import { create } from 'zustand';
import { toast } from 'react-toastify';
import { debounce } from '../utils/debounceUtils';
import {
  DEFAULT_FOREIGN_KEY,
  DEFAULT_UNIQUE_KEY,
  DEFAULT_INDEX,
  NON_INDEXABLE_TYPES,
} from '../constants/fieldConstants';
import {
  handleGenerateSQL,
  handleDownloadSQL,
  handleCopySQL,
} from '../utils/SqlActionUtils';
import {
  handleGenerateXML,
  handleDownloadXML,
  handleCopyXML,
} from '../utils/XmlActionUtils';
import {
  handleGenerateJSON,
  handleDownloadJSON,
  handleCopyJSON,
} from '../utils/JsonActionUtils';
import { queryToSchema } from '../utils/QueryToSchemaActionUtils';

const useSchemaStore = create((set, get) => ({
  // ==================== STATE ====================

  // Schema Fields
  fields: [
    { name: '', type: 'varchar', length: 255, identity: false },
  ],

  // Table Metadata
  tableName: '',
  tableComment: '',
  tableCommentAdded: false,
  showAdvanced: [false],

  // Constraints
  foreignKeys: [],
  uniqueKeys: [],
  indices: [],

  // Table Options
  tableEngine: '',
  tableResource: '',
  migrateTable: '',

  // Generated Outputs
  xmlOutput: '',
  mysqlOutput: '',
  jsonOutput: '',
  // keep a copy of the last generated mysql output so Load Last can restore after reset
  lastSavedMysqlOutput: '',

  // UI Errors
  lastError: '',

  // Query Builder Mode
  queryBuilder: false,
  mysqlQuery: '',
  queryError: false,

  // Theme (safe initialization - checks if localStorage is available)
  darkMode: typeof localStorage !== 'undefined' && localStorage.getItem('theme') === 'dark',

  // ==================== FIELD ACTIONS ====================

  /**
   * Add a new empty field after the specified index
   */
  addField: (index) => {
    set((state) => ({
      fields: [
        ...state.fields.slice(0, index + 1),
        { name: '', type: 'varchar', length: 255, identity: false },
        ...state.fields.slice(index + 1),
      ],
      showAdvanced: [
        ...state.showAdvanced.slice(0, index + 1),
        false,
        ...state.showAdvanced.slice(index + 1),
      ],
    }));
  },

  /**
   * Remove a field at the specified index
   */
  removeField: (index) => {
    set((state) => {
      if (state.fields.length > 1) {
        return {
          fields: state.fields.filter((_, i) => i !== index),
          showAdvanced: state.showAdvanced.filter((_, i) => i !== index),
        };
      }
      return state;
    });
  },

  /**
   * Update a field's properties
   */
  updateField: (index, event) => {
    const { name, type, value, checked } = event.target;

    set((state) => {
      const newFields = [...state.fields];
      const field = newFields[index];

      if (type === 'checkbox') {
        field[name] = checked;
      } else if (type === 'number') {
        field[name] = value ? parseInt(value, 10) : '';
      } else {
        field[name] = value;
      }

      // Clear length for types that don't need it
      if (name === 'type' && !['varchar', 'char', 'decimal'].includes(value)) {
        field.length = '';
      }

      // Handle nullable for foreign keys
      if (name === 'nullable' && checked) {
        // Check if this field is part of a FK reference
        const isFKRef = state.foreignKeys.some(
          (fk) => fk.referenceColumn === field.name
        );
        if (isFKRef) {
          const msg = 'Cannot make this column nullable because it is referenced by a foreign key. Remove the foreign key first.';
          toast.error(msg);
          set({ lastError: msg });
          return state;
        }
      }

      return { fields: newFields };
    });
  },

  /**
   * Toggle advanced options for a field
   */
  toggleAdvanced: (index) => {
    set((state) => {
      const newShowAdvanced = [...state.showAdvanced];
      newShowAdvanced[index] = !newShowAdvanced[index];
      return { showAdvanced: newShowAdvanced };
    });
  },

  // Clear last shown error
  clearError: () => set({ lastError: '' }),

  // ==================== FOREIGN KEY ACTIONS ====================

  /**
   * Add a new empty foreign key constraint
   */
  addForeignKey: () => {
    set((state) => {
      // Check if there are any incomplete FKs
      const incompleteFK = state.foreignKeys.find(
        (fk) =>
          !fk.currentColumn ||
          !fk.referenceTable ||
          !fk.referenceColumn
      );

      if (incompleteFK) {
        const msg = 'Please finish the current foreign key (fill all fields) before adding another.';
        toast.error(msg);
        set({ lastError: msg });
        return state;
      }

      return {
        foreignKeys: [...state.foreignKeys, { ...DEFAULT_FOREIGN_KEY }],
      };
    });
  },

  /**
   * Remove a foreign key constraint at the specified index
   */
  removeForeignKey: (index) => {
    set((state) => ({
      foreignKeys: state.foreignKeys.filter((_, i) => i !== index),
    }));
  },

  /**
   * Update a foreign key constraint
   */
  updateForeignKey: (index, event) => {
    const { name, value } = event.target;

    set((state) => {
      const newForeignKeys = [...state.foreignKeys];
      newForeignKeys[index][name] = value;
      return { foreignKeys: newForeignKeys };
    });
  },

  // ==================== UNIQUE KEY ACTIONS ====================

  /**
   * Add a new empty unique key constraint
   */
  addUniqueKey: () => {
    set((state) => {
      // Check if there are any incomplete UKs
      const incompleteUK = state.uniqueKeys.find(
        (uk) => !uk.uniqueColumns || uk.uniqueColumns.length === 0
      );

      if (incompleteUK) {
        const msg = 'Please finish editing the current unique key before adding a new one.';
        toast.error(msg);
        set({ lastError: msg });
        return state;
      }

      return {
        uniqueKeys: [...state.uniqueKeys, { ...DEFAULT_UNIQUE_KEY, uniqueColumns: [] }],
      };
    });
  },

  /**
   * Remove a unique key constraint at the specified index
   */
  removeUniqueKey: (index) => {
    set((state) => ({
      uniqueKeys: state.uniqueKeys.filter((_, i) => i !== index),
    }));
  },

  /**
   * Update a unique key constraint
   */
  updateUniqueKey: (index, selectedValues) => {
    set((state) => {
      const newUniqueKeys = [...state.uniqueKeys];
      newUniqueKeys[index].uniqueColumns = selectedValues;
      return { uniqueKeys: newUniqueKeys };
    });
  },

  // ==================== INDEX ACTIONS ====================

  /**
   * Add a new empty index
   */
  addIndex: () => {
    set((state) => {
      // Check if there are any incomplete indices
      const incompleteIndex = state.indices.find(
        (idx) => !idx.columnsToIndex || idx.columnsToIndex.length === 0
      );

      if (incompleteIndex) {
        const msg = 'Please finish editing the current index before adding another.';
        toast.error(msg);
        set({ lastError: msg });
        return state;
      }

      return {
        indices: [...state.indices, { ...DEFAULT_INDEX, columnsToIndex: [] }],
      };
    });
  },

  /**
   * Remove an index at the specified index
   */
  removeIndex: (index) => {
    set((state) => ({
      indices: state.indices.filter((_, i) => i !== index),
    }));
  },

  /**
   * Update index columns or type
   */
  updateIndex: (index, selectedValue, isTypeChange = false) => {
    set((state) => {
      const newIndices = [...state.indices];

      if (isTypeChange) {
        // Update index type (btree, fulltext, hash)
        newIndices[index].indexType = selectedValue;
      } else {
        // Update columns to index
        // Check if trying to index non-indexable types
        if (
          Array.isArray(selectedValue) &&
          selectedValue.some((value) => {
            const field = state.fields.find((f) => f.name === value);
            return field && NON_INDEXABLE_TYPES.includes(field.type);
          })
        ) {
          const msg = 'Cannot create an index on text/blob/json fields — choose a different column.';
          toast.error(msg);
          set({ lastError: msg });
          return state;
        }

        newIndices[index].columnsToIndex = selectedValue;
      }

      return { indices: newIndices };
    });
  },

  // ==================== TABLE METADATA ACTIONS ====================

  /**
   * Update table name
   */
  updateTableName: (tableName) => {
    set({ tableName });
  },

  /**
   * Update table comment
   */
  updateTableComment: (tableComment) => {
    set({ tableComment });
  },

  /**
   * Toggle table comment visibility
   */
  toggleTableComment: () => {
    set((state) => ({
      tableCommentAdded: !state.tableCommentAdded,
    }));
  },

  /**
   * Update table engine
   */
  updateTableEngine: (tableEngine) => {
    set({ tableEngine });
  },

  /**
   * Update table resource
   */
  updateTableResource: (tableResource) => {
    set({ tableResource });
  },

  /**
   * Update migrate table flag
   */
  updateMigrateTable: (migrateTable) => {
    set({ migrateTable });
  },

  /**
   * Remove all table metadata
   */
  removeTableMetadata: () => {
    set({
      tableCommentAdded: false,
      tableEngine: '',
      migrateTable: '',
      tableComment: '',
      tableResource: '',
    });
  },

  // ==================== QUERY BUILDER ACTIONS ====================

  /**
   * Toggle query builder mode
   */
  toggleQueryBuilder: () => {
    set((state) => ({ queryBuilder: !state.queryBuilder }));
  },

  /**
   * Set query builder mode explicitly
   */
  setQueryBuilderMode: (enabled) => {
    set({ queryBuilder: enabled });
  },

  /**
   * Update MySQL query input
   */
  updateMysqlQuery: (mysqlQuery) => {
    set({ mysqlQuery });
  },

  /**
   * Parse SQL query and populate schema
   */
  parseQuery: () => {
    const state = get();
    if (state.queryBuilder && state.mysqlQuery) {
      queryToSchema(
          state.mysqlQuery,
          (tableName) => set({ tableName }),
          (indices) => set({ indices }),
          (foreignKeys) => set({ foreignKeys }),
          (uniqueKeys) => set({ uniqueKeys }),
          (tableEngine) => set({ tableEngine }),
          (tableComment) => set({ tableComment }),
          (fields) => set({ fields }),
          (xmlOutput) => set({ xmlOutput }),
          (mysqlOutput) => set({ mysqlOutput, lastSavedMysqlOutput: mysqlOutput }),
          (jsonOutput) => set({ jsonOutput }),
          (error) => set({ queryError: error })
        );
    }
  },

  /**
   * Toggle dark mode theme
   */
  toggleDarkMode: () => {
    set((state) => {
      const newDarkMode = !state.darkMode;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
      }
      // Update document immediately for visual feedback
      document.documentElement.setAttribute('data-theme', newDarkMode ? 'dark' : 'light');
      document.body.classList.toggle('dark-mode', newDarkMode);
      return { darkMode: newDarkMode };
    });
  },

  // ==================== OUTPUT GENERATION ====================

  /**
   * Generate all outputs (XML, SQL, JSON)
   */
  generateAllOutputs: debounce(() => {
    const state = get();

    // Only generate if we have a table name and at least one field
    if (!state.tableName || !state.fields.some((f) => f.name)) {
      return;
    }

    // Don't generate in query builder mode
    if (state.queryBuilder) {
      return;
    }

    // Generate SQL
    handleGenerateSQL(
      state.fields,
      state.tableName,
      state.foreignKeys,
      state.uniqueKeys,
      state.indices,
      state.tableComment,
      state.tableEngine,
      (mysqlOutput) => set({ mysqlOutput, lastSavedMysqlOutput: mysqlOutput })
    );

    // Generate XML
    handleGenerateXML(
      state.fields,
      state.tableName,
      state.foreignKeys,
      state.uniqueKeys,
      state.indices,
      state.migrateTable,
      state.tableComment,
      state.tableResource,
      state.tableEngine,
      (xmlOutput) => set({ xmlOutput })
    );

    // Generate JSON
    handleGenerateJSON(
      state.fields,
      state.indices,
      state.tableName,
      state.foreignKeys,
      (jsonOutput) => set({ jsonOutput })
    );
  }, 300),

  /**
   * Download XML output
   */
  downloadXML: () => {
    const state = get();
    handleDownloadXML(state.xmlOutput);
  },

  /**
   * Copy XML output to clipboard
   */
  copyXML: () => {
    const state = get();
    return handleCopyXML(state.xmlOutput);
  },

  /**
   * Download SQL output
   */
  downloadSQL: () => {
    const state = get();
    handleDownloadSQL(state.tableName, state.mysqlOutput);
  },

  /**
   * Copy SQL output to clipboard
   */
  copySQL: () => {
    const state = get();
    return handleCopySQL(state.mysqlOutput);
  },

  /**
   * Download JSON output
   */
  downloadJSON: () => {
    const state = get();
    handleDownloadJSON(state.jsonOutput);
  },

  /**
   * Copy JSON output to clipboard
   */
  copyJSON: () => {
    const state = get();
    return handleCopyJSON(state.jsonOutput);
  },

  // ==================== UTILITY ACTIONS ====================

  /**
   * Reset all state to initial values
   */
  resetAll: () => {
    set({
      fields: [{ name: '', type: 'varchar', length: 255, identity: false }],
      tableName: '',
      tableComment: '',
      tableCommentAdded: false,
      showAdvanced: [false],
      foreignKeys: [],
      uniqueKeys: [],
      indices: [],
      tableEngine: '',
      tableResource: '',
      migrateTable: '',
      xmlOutput: '',
      mysqlOutput: '',
      jsonOutput: '',
      mysqlQuery: '',
      queryError: false,
    });
    toast.info('Schema reset successfully');
  },

  /**
   * Load last generated MySQL output and populate the form from it
   */
  loadLastFromMysql: () => {
    const state = get();
    const sqlToLoad = (state.mysqlOutput && state.mysqlOutput.trim()) ? state.mysqlOutput : (state.lastSavedMysqlOutput && state.lastSavedMysqlOutput.trim()) ? state.lastSavedMysqlOutput : '';
    if (!sqlToLoad) {
      toast.info('No MySQL output available to load');
      return;
    }
    // Use existing queryToSchema utility to parse the SQL and populate store
    queryToSchema(
      sqlToLoad,
      (tableName) => set({ tableName }),
      (indices) => set({ indices }),
      (foreignKeys) => set({ foreignKeys }),
      (uniqueKeys) => set({ uniqueKeys }),
      (tableEngine) => set({ tableEngine }),
      (tableComment) => set({ tableComment }),
      (fields) => set({ fields }),
      (xmlOutput) => set({ xmlOutput }),
          (mysqlOutput) => set({ mysqlOutput, lastSavedMysqlOutput: mysqlOutput }),
      (jsonOutput) => set({ jsonOutput }),
      (error) => set({ queryError: error })
    );
    toast.info('Loaded schema from last MySQL output');
  },
}));

export default useSchemaStore;
